/**
 * PaymentReminderConfirmDialog — Confirmation dialog before sending
 * payment reminder emails to borrowers.
 *
 * Shows a summary of candidates (upcoming/overdue breakdown),
 * a scrollable list of loans with checkboxes for selection,
 * and a send confirmation button.
 * Used by both bulk (dashboard) and individual (loan detail) flows.
 */

import { useState, useEffect } from 'react';
import { Loader2, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/components/banker-recon/types';
import { formatDateDisplay } from '@/utils/dateHelpers';
import type { PaymentReminderLoan } from '@/types/servicing';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PaymentReminderConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: PaymentReminderLoan[];
  onConfirm: (selected: PaymentReminderLoan[]) => Promise<void>;
  sending: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PaymentReminderConfirmDialog({
  open,
  onOpenChange,
  candidates,
  onConfirm,
  sending
}: PaymentReminderConfirmDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset selection to all-selected when dialog opens or candidates change
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(candidates.map(c => c.servicingLoanId)));
    }
  }, [open, candidates]);

  const selectedCount = selectedIds.size;
  const allSelected = selectedCount === candidates.length && candidates.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < candidates.length;

  const selectedCandidates = candidates.filter(c => selectedIds.has(c.servicingLoanId));
  const overdueCount = selectedCandidates.filter(c => c.paymentStatus === 'overdue').length;
  const upcomingCount = selectedCandidates.filter(c => c.paymentStatus === 'upcoming').length;

  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      setSelectedIds(new Set(candidates.map(c => c.servicingLoanId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleConfirm = () => {
    if (selectedCount === 0) {
return;
}
    onConfirm(selectedCandidates);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Payment Reminders
          </DialogTitle>
          <DialogDescription>
            {selectedCount} of {candidates.length} borrower{candidates.length !== 1 ? 's' : ''} selected
            to receive a payment reminder email.
          </DialogDescription>
        </DialogHeader>

        {/* Summary badges */}
        <div className="flex items-center gap-2 mt-1">
          {overdueCount > 0 && (
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
              {overdueCount} overdue
            </Badge>
          )}
          {upcomingCount > 0 && (
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
              {upcomingCount} upcoming
            </Badge>
          )}
        </div>

        {/* Scrollable loan list */}
        <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
          {/* Select All header */}
          {candidates.length > 1 && (
            <div className="flex items-center gap-3 px-3 py-2 bg-muted/50 sticky top-0 z-10">
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
                disabled={sending}
                aria-label="Select all loans"
              />
              <span className="text-xs font-medium text-muted-foreground">
                {allSelected ? 'Deselect All' : 'Select All'}
              </span>
            </div>
          )}

          {candidates.map((c) => (
            <div
              key={c.servicingLoanId}
              className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                selectedIds.has(c.servicingLoanId) ? '' : 'opacity-50'
              }`}
            >
              <Checkbox
                checked={selectedIds.has(c.servicingLoanId)}
                onCheckedChange={() => toggleOne(c.servicingLoanId)}
                disabled={sending}
                aria-label={`Select loan ${c.loanNumber}`}
              />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{c.loanNumber}</div>
                <div className="text-muted-foreground text-xs truncate">{c.borrowerName}</div>
              </div>
              <div className="text-right ml-3 shrink-0">
                <div className="font-medium">{formatCurrency(c.amountDue)}</div>
                <div className="text-xs text-muted-foreground">
                  {formatDateDisplay(c.dueDate)}
                </div>
              </div>
              <div className="ml-3 shrink-0">
                <Badge
                  variant="outline"
                  className={
                    c.paymentStatus === 'overdue'
                      ? 'bg-red-100 text-red-800 border-red-200'
                      : 'bg-amber-100 text-amber-800 border-amber-200'
                  }
                >
                  {c.paymentStatus === 'overdue'
                    ? `${c.daysOverdue}d overdue`
                    : 'upcoming'}
                </Badge>
              </div>
            </div>
          ))}
        </div>

        {/* CC notice */}
        <p className="text-xs text-muted-foreground mt-1">
          Loan officers and servicing@lendwisemtg.com will be CC'd on each email.
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={sending || selectedCount === 0}
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send {selectedCount} Reminder{selectedCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
